import React from 'react';
import { ActiveFilter } from '../../crawler/crawler.interface';
import JobDetailFilters from '../../crawler/pages/jobDetail/jobDetailFilters/jobDetailFilters';
import JobListFilters from '../../crawler/pages/jobList/jobListFilters/jobListFilters';
import { outsideClick } from '../../utils/hooks';
import Button from '../button/button';
import FilterTag from '../filterTag/filterTag';
import { DownloadArrow, PlusIcon, RemoveIcon } from '../icons/icons';
import './filterSection.scss';
import FloatingMessage, { FloatingMessageState } from '../floatingMessage/floatingMessage';
import { __assignFilterStringDetailView } from 'src/app/utils/filters';
import { _generatePlineExportForSelectedPages, _generateQAExportByFilter, _generateSocialLinkExport } from 'src/app/crawler/services/report.services';

export interface FilterSectionProps {
    activeFilters: ActiveFilter[];
    pageType: string;
    filterOptions?: string[];
    domainOptions?: string[];
    phraseOptions?: string[];
    jobId?: number;
    jobName?: string;
    crawlfishUsers?: string[];
    handleFilterRemove?: (filter: ActiveFilter) => void;
    handleFilterSubmit?: (filter: ActiveFilter) => void;
    clearActiveFilters?: () => void;
}

function FilterSection(
    {
        activeFilters,
        filterOptions = [],
        pageType,
        domainOptions = [],
        phraseOptions = [],
        crawlfishUsers = [],
        jobId,
        jobName,
        handleFilterRemove,
        handleFilterSubmit,
        clearActiveFilters,
    }: FilterSectionProps) {

    const [isJobFilterDropdownOpen, setJobFilterDropdownOpen] = React.useState<boolean>(false);
    const [isPageFilterDropdownOpen, setPageFilterDropdownOpen] = React.useState<boolean>(false);
    const [qaExportState, setQaExportState] = React.useState<FloatingMessageState>({} as FloatingMessageState);
    const [plineExportState, setPlineExportState] = React.useState<FloatingMessageState>({} as FloatingMessageState);
    const [socialExportState, setSocialExportState] = React.useState<FloatingMessageState>({} as FloatingMessageState);

    const dropdownRef = React.useRef<HTMLDivElement>(null);
    outsideClick(dropdownRef, closeAllDropdown);

    function removeActiveFilter(filter: ActiveFilter) {
        if (handleFilterRemove) {
            handleFilterRemove(filter);
        }
    }

    function closeAllDropdown() {
        setJobFilterDropdownOpen(false);
        setPageFilterDropdownOpen(false);
    }

    // Filters
    function submitFilterValue(filter: ActiveFilter) {
        if (handleFilterSubmit) {
            handleFilterSubmit(filter);
            setJobFilterDropdownOpen(false);
            setPageFilterDropdownOpen(false);
        }
    }

    function handleOpenDropdown() {
        if (pageType === 'jobs') {
            setJobFilterDropdownOpen(prevState => !prevState);
        } else if (pageType === 'job-detail') {
            setPageFilterDropdownOpen(prevState => !prevState);
        }
    }

    function clearAllFilters() {
        if (clearActiveFilters) {
            clearActiveFilters();
        }
    }

    const filterString = __assignFilterStringDetailView(activeFilters);

    async function generateQaExport(): Promise<void> {
        if (jobId && jobName) {
            setQaExportState({ ...qaExportState, isLoading: true });
            try {
                await _generateQAExportByFilter(jobId, jobName, filterString);
                setQaExportState({
                    message: 'QA Export Complete!',
                    variant: 'success-box right-align',
                    isLoading: false,
                });
            } catch (err) {
                console.error('Failed to generate QA Export. Error: ', err);
                setQaExportState({
                    message: `QA Export Failed. Error: , ${err}`,
                    variant: 'error-box right-align',
                    isLoading: false,
                });
            }
        }
    }

    async function generateSocialLinksExport(): Promise<void> {
        if (jobId && jobName) {
            setSocialExportState({ ...socialExportState, isLoading: true });
            try {
                await _generateSocialLinkExport(jobId, jobName);
                setSocialExportState({
                    message: 'Social Export Complete!',
                    variant: 'success-box left-arrow mr-2',
                    isLoading: false,
                });
            } catch (err) {
                console.error('Failed to generate Social Export. Error: ', err);
                setSocialExportState({
                    message: `Social Export Failed. Error: , ${err}`,
                    variant: 'error-box left-arrow mr-2',
                    isLoading: false,
                });
            }
        }
    }

    async function generatePlineExport(): Promise<void> {
        if (jobId && jobName) {
            setPlineExportState({ ...qaExportState, isLoading: true });
            try {
                await _generatePlineExportForSelectedPages(jobId, jobName);
                setPlineExportState({
                    message: 'PerformLine Export Complete!',
                    variant: 'success-box left-arrow',
                    isLoading: false,
                });
            } catch (err) {
                console.error('Failed to generate PerformLine Export. Error: ', err);
                setPlineExportState({
                    message: `PerformLine Export Failed. Error: , ${err}`,
                    variant: 'error-box left-arrow',
                    isLoading: false,
                });
            }
        }
    }

    function qaExportCallback(): void {
        setQaExportState({} as FloatingMessageState);
    }

    function plExportCallback(): void {
        setPlineExportState({} as FloatingMessageState);
    }

    function socialExportCallback(): void {
        setSocialExportState({} as FloatingMessageState);
    }

    return (
        <div className="filter-section" data-testid="filter-section">
            <div className="grid-wrapper-12 filters">

                <div className="grid-8 active-filters">
                    {activeFilters.length > 0 ?
                        <>
                            {activeFilters.map((filter: ActiveFilter) => (
                                filter.value !== 'clear-filter' ?
                                    <FilterTag key={`${filter.name}-${filter.value}`} filter={filter} removeActiveFilter={removeActiveFilter} />
                                    : null
                            ))}
                        </>
                        :
                        <span className="no-filters">No filters applied</span>}
                </div>
                <div className="grid-4 filter-buttons">
                    <Button variant="add-filter-button" onClick={handleOpenDropdown} cyTestId="add-filter-button">
                        <>
                            <PlusIcon />
                            Add filter
                        </>
                    </Button>
                    <Button variant="clear-filter-button" onClick={clearAllFilters}>
                        <>
                            <RemoveIcon />
                            <span> Clear all </span>
                        </>
                    </Button>
                </div>
                {filterOptions &&
                    <div className="dropdown-wrapper">
                        {isJobFilterDropdownOpen &&
                            <JobListFilters activeFilters={activeFilters} isFilterDropdownOpen={isJobFilterDropdownOpen} submitFilterValue={submitFilterValue}
                                filterOptions={filterOptions} dropdownRef={dropdownRef} crawlfishUsers={crawlfishUsers} />
                        }
                        {isPageFilterDropdownOpen &&
                            <JobDetailFilters activeFilters={activeFilters} isFilterDropdownOpen={isPageFilterDropdownOpen} submitFilterValue={submitFilterValue}
                                filterOptions={filterOptions} dropdownRef={dropdownRef} domainOptions={domainOptions} phraseOptions={phraseOptions} />
                        }
                    </div>
                }
            </div>
            <div className="grid-12 export-pages">
                {pageType === 'job-detail' &&
                    <>
                        <a className={"pl-social-links-export-link" + (qaExportState.isLoading ? ' loading' : '')}
                            onClick={generateSocialLinksExport}
                            title={socialExportState.isLoading ? "Creating Social Links Export..." : "Generates a report of social links for this job."}
                            data-testid="social-link-export"
                        >
                            <DownloadArrow />
                            <span>Social Links Export</span>
                        </a>

                        <a className="pl-export-pages-link"
                            onClick={generatePlineExport}
                            title={plineExportState.isLoading ? "Creating Pline Export..." : "Generates a report of selected pages. Limit of 10,000 pages."}
                            data-testid="pline-export"
                        >
                            <DownloadArrow />
                            <span>PerformLine Export</span>
                        </a>
                        <a className={"qa-export-pages-link" + (qaExportState.isLoading ? ' loading' : '')}
                            onClick={generateQaExport}
                            title={qaExportState.isLoading ? "Creating QA Export..." : "Generates a report of any completed pages given the current filters. Limit of 1,000,000 pages."}
                            data-testid="qa-export"
                        >
                            <DownloadArrow />
                            <span>QA Export</span>
                        </a>
                        {qaExportState && qaExportState.message?.length > 0 ?
                            <FloatingMessage message={qaExportState.message} variant={qaExportState.variant} timeOut={1500} cb={qaExportCallback} />
                            : null
                        }
                        {plineExportState && plineExportState.message?.length > 0 ?
                            <FloatingMessage message={plineExportState.message} variant={plineExportState.variant} timeOut={1500} cb={plExportCallback} />
                            : null
                        }
                        {socialExportState && socialExportState.message?.length > 0 ?
                            <FloatingMessage message={socialExportState.message} variant={socialExportState.variant} timeOut={1500} cb={socialExportCallback} />
                            : null
                        }
                    </>
                }
            </div>

        </div>
    );
}

export default FilterSection;